<script setup lang="ts">
import { ref, computed, reactive } from 'vue'
import agentsHttpService from '@/services/http/agentsHttpService'
import diagnosticsHttpService from '@/services/http/diagnosticsHttpService'
import type {
  AgentRecommendationModel,
  AllAgentRecommendationModel,
  RecommendationTestModel,
} from '@/services/http/httpResponses'
import { Roles } from '@/constants'
import { z } from 'zod'
import { zodResolver } from '@primevue/forms/resolvers/zod'
import jwtService from '@/services/jwtService'
import { jwtDecode } from 'jwt-decode'
import type { JwtPayload } from '@/types/auth'
import type { Id } from '@raildev/shared-utils'

definePage({
  meta: { allowedRoles: [Roles.Agent, Roles.Admin] },
})

const token = jwtService.getToken()
const currentRole = token ? (jwtDecode<JwtPayload>(token).role as Roles) : null
const isAdmin = computed(() => currentRole === Roles.Admin)

const rec = ref<AgentRecommendationModel[] | AllAgentRecommendationModel[]>([])
const showRecommendationDialog = ref(false)
const form = reactive<{
  offerPrice: number | null
  validFrom: Date | null
  validTo: Date | null
  maxTests: number | null
  description: string | null
}>({
  offerPrice: null,
  validFrom: null,
  validTo: null,
  maxTests: null,
  description: null,
})
const appUrl = ref<string>('')
const buildLink = (recItem: AgentRecommendationModel) => {
  if (!appUrl.value) return ''
  const url = new URL('/welcome', appUrl.value)
  url.searchParams.set('agentId', recItem.id)
  return url.toString()
}

const recommendationSchema = z
  .object({
    offerPrice: z.number().positive({ message: 'Цена должна быть > 0' }).nullable().optional(),
    validFrom: z.date().nullable().optional(),
    validTo: z.date().nullable().optional(),
    maxTests: z.number().int().positive({ message: 'Должно быть целое > 0' }).nullable().optional(),
    description: z.string().max(250, { message: 'До 250 символов' }).nullable().optional(),
  })
  .refine(
    (v) => {
      if (v.validFrom && v.validTo) {
        return v.validTo.getTime() >= v.validFrom.getTime()
      }
      return true
    },
    { message: 'Дата окончания должна быть не раньше даты начала', path: ['validTo'] },
  )

const formResolver = zodResolver(recommendationSchema)
const formInitialValues = computed(() => ({
  offerPrice: form.offerPrice,
  validFrom: form.validFrom,
  validTo: form.validTo,
  maxTests: form.maxTests,
  description: form.description ?? null,
}))

const load = async () => {
  await Promise.allSettled([
    (async () => {
      if (isAdmin.value) {
        const { recommendations } = await agentsHttpService.getAllAgentRecommendations()
        rec.value = recommendations
      } else {
        const { recommendations } = await agentsHttpService.getAgentRecommendations()
        rec.value = recommendations
      }
    })(),
    diagnosticsHttpService.getAppSettings().then((r) => {
      appUrl.value = r.notifications.appUrl
    }),
  ])
}

const openRecommendationDialog = () => {
  form.offerPrice = null
  form.validFrom = null
  form.validTo = null
  form.maxTests = null
  form.description = null
  showRecommendationDialog.value = true
}

const createRecommendation = async ({
  valid,
  values,
}: {
  valid: boolean
  values: {
    offerPrice: number | null
    validFrom: Date | null
    validTo: Date | null
    maxTests: number | null
    description: string | null
  }
}) => {
  if (!valid) {
    return
  }

  try {
    const { data } = await agentsHttpService.createRecommendation({
      offerPrice: values.offerPrice ?? null,
      validFrom: values.validFrom ?? null,
      validTo: values.validTo ?? null,
      maxTests: values.maxTests ?? null,
      description: (values.description ?? '')?.trim() || null,
    })

    const newRow: AgentRecommendationModel = {
      id: data.id,
      shortId: '',
      createdAt: new Date(),
      offerPrice: values.offerPrice ?? null,
      validFrom: values.validFrom ?? null,
      validTo: values.validTo ?? null,
      maxTests: values.maxTests ?? null,
      description: (values.description ?? '')?.trim() || null,
      paidTestsCount: 0,
      totalTestsCount: 0,
    }

    ;(rec.value as AgentRecommendationModel[]).unshift(newRow)
    showRecommendationDialog.value = false

    const toShare = buildLink(newRow)
    if (navigator.share) {
      await navigator.share({ url: toShare })
    } else {
      await navigator.clipboard.writeText(toShare)
    }
  } catch (error) {
    console.error("Can't recommend:", error)
  }
}

const copy = async (text: string) => {
  await navigator.clipboard.writeText(text)
}

const showTestsDialog = ref(false)
const testsDialogTitle = ref('')
const recommendationTests = ref<RecommendationTestModel[]>([])
const testsLoading = ref(false)

const testStatusLabel = (status: number) => {
  const map: Record<number, string> = {
    0: 'Не создан',
    1: 'Создан',
    2: 'Генерация вопросов',
    3: 'Начат',
    4: 'Генерация результатов',
    5: 'Завершён',
  }
  return map[status] ?? String(status)
}

const isActive = (rec: AgentRecommendationModel) => {
  const now = new Date()
  if (rec.validFrom && new Date(rec.validFrom) > now) return false
  if (rec.validTo && new Date(rec.validTo) <= now) return false
  return true
}

const pauseRecommendation = async (rec: AgentRecommendationModel) => {
  try {
    await agentsHttpService.pauseRecommendation(rec.id as Id)
    rec.validTo = new Date()
  } catch (e) {
    console.error('Pause failed', e)
  }
}

const openTestsDialog = async (rec: AgentRecommendationModel) => {
  testsDialogTitle.value = `Тесты по ссылке от ${rec.createdAt.toLocaleString('ru-RU')}`
  recommendationTests.value = []
  showTestsDialog.value = true
  testsLoading.value = true
  try {
    const res = await agentsHttpService.getRecommendationTests(rec.id as Id)
    recommendationTests.value = res.tests
  } finally {
    testsLoading.value = false
  }
}

await load()
</script>

<template>
  <div class="flex flex-col gap-4 w-full">
    <Toast />
    <div class="flex justify-between items-center">
      <h2 class="text-2xl font-semibold">Реферальные ссылки</h2>
      <Button label="Создать ссылку" @click="openRecommendationDialog" />
    </div>

    <DataTable ref="dt" :value="rec" dataKey="id" responsiveLayout="scroll">
      <Column header="Дата" style="min-width: 12rem">
        <template #body="slotProps">
          {{ slotProps.data.createdAt.toLocaleString('ru-RU') }}
        </template>
      </Column>

      <Column v-if="isAdmin" header="Агент" style="min-width: 14rem">
        <template #body="slotProps">
          <p>{{ (slotProps.data as AllAgentRecommendationModel).agent?.name ?? '' }}</p>
          <p>{{ (slotProps.data as AllAgentRecommendationModel).agent?.email ?? '' }}</p>
        </template>
      </Column>

      <Column header="Короткая ссылка" style="min-width: 14rem">
        <template #body="slotProps">
          <div class="flex items-center gap-2">
            <span class="truncate text-sm">{{ `${appUrl}r/${slotProps.data.shortId}` }}</span>
            <Button
              icon="pi pi-copy"
              text
              rounded
              @click="copy(`${appUrl}r/${slotProps.data.shortId}`)"
            />
          </div>
        </template>
      </Column>

      <Column header="Полная ссылка" style="min-width: 20rem">
        <template #body="slotProps">
          <div class="flex items-center gap-2 max-w-[520px]">
            <span class="truncate">{{ buildLink(slotProps.data) }}</span>
            <Button icon="pi pi-copy" text rounded @click="copy(buildLink(slotProps.data))" />
          </div>
        </template>
      </Column>

      <Column header="Цена (KZT)" style="min-width: 8rem">
        <template #body="slotProps">
          {{ slotProps.data.offerPrice ?? '' }}
        </template>
      </Column>

      <Column header="Действителен с" style="min-width: 10rem">
        <template #body="slotProps">
          {{ slotProps.data.validFrom ? slotProps.data.validFrom.toLocaleString('ru-RU') : '' }}
        </template>
      </Column>

      <Column header="Действителен до" style="min-width: 10rem">
        <template #body="slotProps">
          {{ slotProps.data.validTo ? slotProps.data.validTo.toLocaleString('ru-RU') : '' }}
        </template>
      </Column>

      <Column header="Лимит" style="min-width: 8rem">
        <template #body="slotProps">
          {{ slotProps.data.maxTests ?? '' }}
        </template>
      </Column>

      <Column header="Всего тестов" style="min-width: 8rem">
        <template #body="slotProps">
          <Tag
            :value="String(slotProps.data.totalTestsCount)"
            severity="success"
            class="cursor-pointer"
            @click="openTestsDialog(slotProps.data)"
          />
        </template>
      </Column>

      <Column header="Оплачено" style="min-width: 8rem">
        <template #body="slotProps">
          {{ slotProps.data.paidTestsCount }}
        </template>
      </Column>

      <Column header="Описание" style="min-width: 16rem">
        <template #body="slotProps">
          {{ slotProps.data.description ?? '' }}
        </template>
      </Column>

      <Column header="Статус" style="min-width: 9rem">
        <template #body="slotProps">
          <Tag
            :value="isActive(slotProps.data) ? 'Активна' : 'Не активна'"
            :severity="isActive(slotProps.data) ? 'success' : 'danger'"
          />
        </template>
      </Column>

      <Column header="Действия" style="min-width: 10rem">
        <template #body="slotProps">
          <Button
            v-if="isActive(slotProps.data)"
            label="Приостановить"
            @click="pauseRecommendation(slotProps.data)"
          />
        </template>
      </Column>
      <template #empty>
        <div class="flex flex-col items-center gap-2 py-6 text-gray-400">
          <i class="pi pi-inbox text-4xl" />
          <span>Данные отсутствуют</span>
        </div>
      </template>
    </DataTable>

    <Dialog
      v-model:visible="showRecommendationDialog"
      header="Реферальная ссылка"
      :modal="true"
      :class="'w-[90vw] sm:w-[600px]'"
      :closable="true"
    >
      <Form
        v-slot="$form"
        :resolver="formResolver"
        :initialValues="formInitialValues"
        @submit="createRecommendation"
        class="flex flex-col gap-4"
      >
        <p class="text-sm text-gray-600">
          Все поля необязательны. Если не указывается цена, то будет использована базовая цена
          продукта. Если указать 0 — тест будет бесплатным.
        </p>

        <div class="flex flex-col gap-1">
          <label for="offerPrice">Цена по ссылке (KZT, опционально)</label>
          <InputNumber
            id="offerPrice"
            name="offerPrice"
            :min="0"
            mode="decimal"
            :maxFractionDigits="2"
            :useGrouping="false"
            :inputMode="'decimal'"
            :locale="'en-US'"
          />
          <Message v-if="$form.offerPrice?.invalid" severity="error" size="small" variant="simple">
            {{ $form.offerPrice?.error?.message }}
          </Message>
        </div>

        <div class="flex flex-col gap-1">
          <label for="validFrom">Действительно с (опционально)</label>
          <Calendar
            id="validFrom"
            name="validFrom"
            date-format="dd.mm.yy"
            showIcon
            :showTime="false"
          />
          <Message v-if="$form.validFrom?.invalid" severity="error" size="small" variant="simple">
            {{ $form.validFrom?.error?.message }}
          </Message>
        </div>

        <div class="flex flex-col gap-1">
          <label for="validTo">Действительно до (опционально)</label>
          <Calendar id="validTo" name="validTo" date-format="dd.mm.yy" showIcon :showTime="false" />
          <Message v-if="$form.validTo?.invalid" severity="error" size="small" variant="simple">
            {{ $form.validTo?.error?.message }}
          </Message>
        </div>

        <div class="flex flex-col gap-1">
          <label for="maxTests">Максимальное количество тестов (опционально)</label>
          <InputNumber
            id="maxTests"
            name="maxTests"
            :min="1"
            mode="decimal"
            :useGrouping="false"
            :inputMode="'decimal'"
            :locale="'en-US'"
          />
          <Message v-if="$form.maxTests?.invalid" severity="error" size="small" variant="simple">
            {{ $form.maxTests?.error?.message }}
          </Message>
        </div>

        <div class="flex flex-col gap-1">
          <label for="description">Описание акции (опционально)</label>
          <Textarea
            id="description"
            name="description"
            auto-resize
            rows="3"
            placeholder="Например: «Скидка для выпускников моей школы»"
          />
          <Message v-if="$form.description?.invalid" severity="error" size="small" variant="simple">
            {{ $form.description?.error?.message }}
          </Message>
        </div>

        <div class="flex gap-2">
          <Button class="w-full" label="Отмена" text @click="showRecommendationDialog = false" />
          <Button class="w-full" type="submit" label="Создать" />
        </div>
      </Form>
    </Dialog>
    <Dialog
      v-model:visible="showTestsDialog"
      :header="testsDialogTitle"
      :modal="true"
      class="w-[95vw] sm:w-[800px]"
      :closable="true"
    >
      <div v-if="testsLoading" class="flex justify-center py-8">
        <ProgressSpinner />
      </div>
      <DataTable v-else :value="recommendationTests" dataKey="id" responsiveLayout="scroll">
        <template #empty>
          <div class="text-center py-4 text-gray-500">Тестов по этой ссылке нет</div>
        </template>
        <Column header="Клиент" style="min-width: 14rem">
          <template #body="slotProps">
            <p class="font-medium">{{ slotProps.data.userName ?? '—' }}</p>
            <p class="text-sm text-gray-500">{{ slotProps.data.userEmail ?? '—' }}</p>
          </template>
        </Column>
        <Column header="Начат" style="min-width: 12rem">
          <template #body="slotProps">
            {{ new Date(slotProps.data.startDateTime).toLocaleString('ru-RU') }}
          </template>
        </Column>
        <Column header="Завершён" style="min-width: 12rem">
          <template #body="slotProps">
            {{
              slotProps.data.endDateTime
                ? new Date(slotProps.data.endDateTime).toLocaleString('ru-RU')
                : '—'
            }}
          </template>
        </Column>
        <Column header="Статус" style="min-width: 10rem">
          <template #body="slotProps">
            {{ testStatusLabel(slotProps.data.status) }}
          </template>
        </Column>
        <Column header="Цена (KZT)" style="min-width: 8rem">
          <template #body="slotProps">
            {{ slotProps.data.offerPrice ?? '—' }}
          </template>
        </Column>
        <Column header="Оплата" style="min-width: 8rem">
          <template #body="slotProps">
            <Tag
              :value="slotProps.data.isPaid ? 'Оплачен' : 'Не оплачен'"
              :severity="slotProps.data.isPaid ? 'success' : 'warn'"
            />
          </template>
        </Column>
      </DataTable>
    </Dialog>
  </div>
</template>

<style scoped></style>
